import { action, makeObservable, observable, runInAction, reaction } from "mobx";

import { useEffect } from "react";
import { useMatch } from "react-router";
import { isString } from "lodash-es";
import { AppSubStore, AppSubStoreArgs } from "@/store/types";
import { appRoutes } from "@/app/router";
import { validationModule } from "@/modules/validation";
import { ChatMessageContextKind } from "@/store/chat/types";

export class AppStoreRoutingStore extends AppSubStore {
  noteIdParam: string | null;
  templateIdParam: string | null;
  collectionIdParam: string | null;
  spaceIdParam: string | null;
  createSpaceIdParam: string | null;
  sessionIdParam: string | null;

  constructor(injectedDeps: AppSubStoreArgs) {
    super(injectedDeps);

    this.noteIdParam = null;
    this.templateIdParam = null;
    this.collectionIdParam = null;
    this.templateIdParam = null;
    this.spaceIdParam = null;
    this.createSpaceIdParam = null;
    this.sessionIdParam = null;

    makeObservable(this, {
      noteIdParam: observable,
      templateIdParam: observable,
      collectionIdParam: observable,
      spaceIdParam: observable,
      createSpaceIdParam: observable,
      sessionIdParam: observable,
      setNoteIdParam: action,
      setTemplateIdParam: action,
      setCollectionIdParam: action,
      setSpaceIdParam: action,
      setCreateSpaceIdParam: action,
      setSessionIdParam: action,
      useSyncRoutingParams: action,
      resetState: action,
    });

    // Manage nav contexts based on route changes
    reaction(
      () => this.noteIdParam,
      (noteId, previousNoteId) => {
        // Remove previous note context if it exists
        if (previousNoteId) {
          this.store.chatConversations.removeNavContext(previousNoteId);
        }
        // Add new note context if we're navigating to a note
        if (noteId) {
          this.store.chatConversations.addNavContext({
            id: noteId,
            kind: ChatMessageContextKind.NoteMention,
          });
        }
      }
    );

    reaction(
      () => this.collectionIdParam,
      (collectionId, previousCollectionId) => {
        // Remove previous collection context if it exists
        if (previousCollectionId) {
          this.store.chatConversations.removeNavContext(previousCollectionId);
        }
        // Add new collection context if we're navigating to a collection
        if (collectionId) {
          this.store.chatConversations.addNavContext({
            id: collectionId,
            kind: ChatMessageContextKind.CollectionMention,
          });
        }
      }
    );
  }

  setNoteIdParam(maybeNoteIdParam: string | undefined) {
    if (!isString(maybeNoteIdParam)) {
      runInAction(() => {
        this.noteIdParam = null;
      });

      return;
    }

    runInAction(() => {
      this.noteIdParam = maybeNoteIdParam;
    });
  }

  setTemplateIdParam(maybeTemplateIdParam: string | undefined) {
    if (!isString(maybeTemplateIdParam)) {
      runInAction(() => {
        this.templateIdParam = null;
      });

      return;
    }

    runInAction(() => {
      this.templateIdParam = maybeTemplateIdParam;
    });
  }

  setSessionIdParam(maybeSessionIdParam: string | undefined) {
    if (!isString(maybeSessionIdParam)) {
      runInAction(() => {
        this.sessionIdParam = null;
      });

      return;
    }

    runInAction(() => {
      this.sessionIdParam = maybeSessionIdParam;
    });
  }

  setCollectionIdParam(maybeCollectionIdParam: string | undefined) {
    if (!isString(maybeCollectionIdParam)) {
      runInAction(() => {
        this.collectionIdParam = null;
      });

      return;
    }

    runInAction(() => {
      this.collectionIdParam = maybeCollectionIdParam;
    });
  }

  setSpaceIdParam(maybeSpaceIdParam?: string) {
    if (!isString(maybeSpaceIdParam) || !validationModule.isUuid(maybeSpaceIdParam)) {
      runInAction(() => {
        this.spaceIdParam = null;
      });

      return;
    }

    runInAction(() => {
      this.spaceIdParam = maybeSpaceIdParam;
    });
  }

  setCreateSpaceIdParam(maybeCreateSpaceIdParam?: string) {
    if (!isString(maybeCreateSpaceIdParam) || !validationModule.isUuid(maybeCreateSpaceIdParam)) {
      runInAction(() => {
        this.createSpaceIdParam = null;
      });

      return;
    }

    runInAction(() => {
      this.createSpaceIdParam = maybeCreateSpaceIdParam;
    });
  }

  /**
   * We need to wire up each parameter independently, based on the target routes.
   */
  useSyncRoutingParams() {
    const noteIdMatch = useMatch(
      appRoutes.notesView({ params: { noteId: ":noteId" }, starPattern: true })
    );

    const templateIdMatch = useMatch(
      appRoutes.templatesView({ params: { templateId: ":templateId" }, starPattern: true })
    );

    const collectionIdMatch = useMatch(
      appRoutes.collectionsView({ params: { collectionId: ":collectionId" }, starPattern: true })
    );

    const sessionIdMatch = useMatch(
      appRoutes.sessionsView({ params: { sessionId: ":sessionId" }, starPattern: true })
    );

    // const spaceIdMatch = useMatch(
    //   memRoutes.spacesRoot({ params: { spaceId: ":spaceId" }, starPattern: true })
    // );

    // const createSpaceIdMatch = useMatch(
    //   memRoutes.spacesCreateFlow({ params: { spaceId: ":spaceId" }, starPattern: true })
    // );

    const maybeNoteIdParam = noteIdMatch?.params.noteId;
    const maybeCollectionIdParam = collectionIdMatch?.params.collectionId;
    const maybeTemplateIdParam = templateIdMatch?.params.templateId;
    const maybeSessionIdParam = sessionIdMatch?.params.sessionId;
    const maybeSpaceIdParam = undefined; //  spaceIdMatch?.params.spaceId;
    const maybeCreateSpaceIdParam = undefined; // createSpaceIdMatch?.params.spaceId;

    useEffect(() => {
      this.setNoteIdParam(maybeNoteIdParam);
    }, [maybeNoteIdParam]);

    useEffect(() => {
      this.setTemplateIdParam(maybeTemplateIdParam);
    }, [maybeTemplateIdParam]);

    useEffect(() => {
      this.setCollectionIdParam(maybeCollectionIdParam);
    }, [maybeCollectionIdParam]);

    useEffect(() => {
      this.setTemplateIdParam(maybeTemplateIdParam);
    }, [maybeTemplateIdParam]);

    useEffect(() => {
      this.setSessionIdParam(maybeSessionIdParam);
    }, [maybeSessionIdParam]);

    useEffect(() => {
      this.setSpaceIdParam(maybeSpaceIdParam);
    }, [maybeSpaceIdParam]);

    useEffect(() => {
      this.setCreateSpaceIdParam(maybeCreateSpaceIdParam);
    }, [maybeCreateSpaceIdParam]);
  }

  resetState() {
    runInAction(() => {
      this.spaceIdParam = null;
    });
  }
}
